import { toErrorEvent } from './normalise';

import type {
  MonitoringConfig,
  MonitoringProperties,
  MonitoringSink,
  MonitoringUser,
} from './types';

export interface CreateMonitoringOptions {
  /** Resolves configuration, or `null` when no sink is requested. */
  loadConfig: () => MonitoringConfig | null;
  /** Builds the sinks named in the config. Unbuildable ones are omitted. */
  createSinks: (config: MonitoringConfig) => MonitoringSink[];
}

export interface Monitoring {
  /** Names of the sinks actually built. Empty means every call is a no-op. */
  readonly sinks: readonly string[];
  reportError: (
    error: unknown,
    properties?: MonitoringProperties,
    options?: { fatal?: boolean },
  ) => void;
  setUser: (id: string, properties?: MonitoringProperties) => void;
  flush: () => void;
  dispose: () => void;
}

const NO_OP: Monitoring = {
  sinks: [],
  reportError: () => undefined,
  setUser: () => undefined,
  flush: () => undefined,
  dispose: () => undefined,
};

/**
 * Builds the monitoring binding over zero or more sinks.
 *
 * With no config, or no sink that could be built, every method is a no-op —
 * the default in local development, unit tests and Playwright, and what makes
 * this safe to merge and deploy before any destination exists.
 *
 * Normalisation and redaction happen once, here, so each sink receives an
 * already-safe event and cannot forget. A sink whose SDK collects errors on its
 * own is responsible for that path separately.
 *
 * Every sink call is isolated: one throwing sink must not stop the others, and
 * must not turn a handled error into an unhandled one. Failures are swallowed
 * rather than logged, because logging an error from the error reporter is how
 * you get a loop.
 */
export const createMonitoring = ({
  loadConfig,
  createSinks,
}: CreateMonitoringOptions): Monitoring => {
  const config = loadConfig();
  if (!config) {
    return NO_OP;
  }

  let sinks: MonitoringSink[];
  try {
    sinks = createSinks(config);
  } catch (error) {
    // A sink that cannot be constructed must not stop the app booting.
    console.warn('[monitoring] failed to build sinks; continuing without them.', error);
    return NO_OP;
  }

  if (sinks.length === 0) {
    return NO_OP;
  }

  const each = (action: (sink: MonitoringSink) => void): void => {
    for (const sink of sinks) {
      try {
        action(sink);
      } catch {
        // Deliberately silent; see the note above about loops.
      }
    }
  };

  return {
    sinks: sinks.map((sink) => sink.name),
    reportError: (error, properties, options) => {
      let event;
      try {
        event = toErrorEvent(error, properties, options);
      } catch {
        // Normalisation is defensive already, but a thrown getter on an exotic
        // payload should lose the report, not the app.
        return;
      }
      each((sink) => sink.report(event));
    },
    setUser: (id, properties) => {
      const user: MonitoringUser = { id, properties };
      each((sink) => sink.setUser?.(user));
    },
    flush: () => each((sink) => sink.flush?.()),
    dispose: () => {
      each((sink) => sink.dispose?.());
      sinks = [];
    },
  };
};
